<script>
    import PropCalculator from './PropCalculator.svelte';
    import Gamelog from './Gamelog.svelte';
    import SeasonAvgTable from './SeasonAvgTable.svelte';
    export let player;
    export let proj_id;
    export let league = "nba";
    export let premium_membership = true;
    export let placeholder_variances;
    // TODO consider passing in the categories as a prop
    let player_stats;
    let variances;
    let projections;
    let player_id = player.id;
    let props;
    let prop_metrics;
    let means = {};
    // Empirical over/under curves from /<league>/fair_odds/<proj_id>/ (null
    // when unavailable, in which case the calculator falls back to a normal curve)
    let fair_odds = null;
    let market_lines = null;
    // NHL goalies and skaters project different stats
    $: is_goalie = league == "nhl" && player.positions.includes("G");
    $: player_id = player.id;
    $: refreshPlayer(player_id);
    function refreshPlayer() {
        let prom1 = getPlayerStats(player_id);
        let prom2 = getProjection(proj_id);
        let prom3 = getFairOdds(proj_id);
        //wait till all promises are resolved - move this to child component
        Promise.all([prom1, prom2, prom3]).then(values => {
            prop_metrics = combineStats(projections, variances);
        });  
    }
    function getFairOdds(proj_id) {
        fair_odds = null;
        return fetch(`/${league}/fair_odds/${proj_id}/`)
            .then(response => response.ok ? response.json() : null)
            .then(data => {
                fair_odds = data && data.stats ? data.stats : null;
                market_lines = data && data.market ? data.market : null;
            })
            .catch(() => { fair_odds = null; market_lines = null; });
    }
    function combineStats(projections, variances) {
        let combined = [];
        props = [];
        for (let stat in projections) {
            let obj = {};
            if (league == "mlb" && ['singles', 'doubles', 'triples', 'homeruns', 'walks'].includes(stat)) {
                continue;
            }
            if (league == "nfl" && ['off interceptions'].includes(stat)) {
                continue;
            }
            if (league == "nhl" && ['shots', 'power play goals', 'power play assists'].includes(stat)) {
                continue;
            }
            props.push(stat);
            obj.name = stat;
            obj.projection = projections[stat];
            // turn variance to standard deviation
            obj.variance = variances[stat];
            if(!obj.variance){
                let stat_name_underscore = stat.replace(/ /g, "_");
                obj.variance = variances[stat_name_underscore];
            }
            obj.sd = Math.sqrt(obj.variance);
            combined.push(obj);
        }
        // Open on the stat the position is bet on: passing for QBs, rushing
        // for RBs, receiving for everyone who catches the ball
        let first = defaultProp(props);
        if (first) {
            props = [first, ...props.filter(p => p !== first)];
        }
        return combined;
    }
    function defaultProp(available) {
        let wanted = [];
        if (league == "nfl") {
            let pos = player.positions || "";
            if (pos.includes("QB")) wanted = ["pass yards"];
            else if (pos.includes("RB")) wanted = ["rush yards"];
            else wanted = ["rec yards"];
        } else if (league == "nba") {
            wanted = ["points"];
        } else if (league == "nhl") {
            wanted = is_goalie ? ["saves"] : ["shots on goal", "points"];
        } else if (league == "mlb") {
            wanted = player.positions.includes("P") ? ["strikeouts"] : ["hits", "total bases"];
        }
        return wanted.find(w => available.includes(w)) || null;
    }
    function getPlayerStats(player_id){
        return fetch(`/${league}/stats/${player_id}/`)
        .then(response => response.json())
        .then(data => {
                //iterate over data
                player_stats = [];
                for(var stats of data){
                    //append fields to player_stats TODO add points here
                    if(league == "nba"){
                        stats.fields.points = parseInt(stats.fields.ftm) + parseInt(stats.fields.two_point_fgm) * 2 + parseInt(stats.fields.three_point_fgm) * 3;
                        stats.fields["fantasy points"] = stats.fields.points + parseInt(stats.fields.rebounds) * 1.2 + parseInt(stats.fields.assists) * 1.5 + parseInt(stats.fields.steals) * 3 + parseInt(stats.fields.blocks) * 3 - parseInt(stats.fields.turnovers);
                    }
                    else if (league == "mlb"){
                        if (player.positions.includes("P")){
                            // calculate fantasy points for pitchers
                            stats.fields["fantasy points"] = parseInt(stats.fields.earned_runs) * -3 + parseInt(stats.fields.innings_pitched) * 3 + parseInt(stats.fields.quality_starts) * 4 + parseInt(stats.fields.strikeouts) * 3 + parseInt(stats.fields.wins) * 6;
                        }
                        else{
                            // calculate fantasy points for batters
                            stats.fields["fantasy points"] = parseInt(stats.fields.singles) * 3 + parseInt(stats.fields.doubles) * 6 + parseInt(stats.fields.triples) * 9 + parseInt(stats.fields.homeruns) * 12 + parseInt(stats.fields.walks) * 3 + parseInt(stats.fields.runs) * 3.2 + parseInt(stats.fields.stolen_bases) * 6 + parseInt(stats.fields.rbis) * 3.5;
                        }
                    }
                    else if(league == "nhl"){
                        if (is_goalie){
                            stats.fields["fantasy points"] = parseInt(stats.fields.wins) * 12 + parseInt(stats.fields.saves) * 0.8 - parseInt(stats.fields.goals_against) * 4 + parseInt(stats.fields.shutouts) * 8;
                        }
                        else{
                            stats.fields["fantasy points"] = parseInt(stats.fields.goals) * 12 + parseInt(stats.fields.assists) * 8 + (parseInt(stats.fields.shots) + parseInt(stats.fields.blocked_shots)) * 1.6 + (parseInt(stats.fields.power_play_goals) + parseInt(stats.fields.power_play_assists)) * 0.5;
                        }
                    }
                    else if(league == "nfl"){
                        stats.fields["fantasy points"] = parseInt(stats.fields.pass_yards) * 0.04 + parseInt(stats.fields.pass_tds) * 4 + parseInt(stats.fields.off_interceptions) * -2 + parseInt(stats.fields.rush_yards) * 0.1 + parseInt(stats.fields.rush_tds) * 6 + parseInt(stats.fields.rec_yards) * 0.1 + parseInt(stats.fields.rec_tds) * 6 + parseInt(stats.fields.fumbles_lost) * -2 * parseInt(stats.fields.rush_2pc) * 2 + parseInt(stats.fields.rec_2pc) * 2 + parseInt(stats.fields.receptions) * .5;
                    }
                    player_stats.push(stats.fields);
                }
                buildPropCalcMetrics();
            }
        );
    }
    function getProjection(proj_id){
        return fetch(`/${league}/projections/${proj_id}/`)
        .then(response => response.json())
        .then(data => {
                //create cats
                let cats = {};
                let sport_cats = [];
                if (league == "nba"){
                    sport_cats = ["points", "rebounds", "assists", "steals", "blocks", "turnovers", "fantasy points"];
                }
                else if (league == "mlb"){
                    if(player.positions.includes('P')){
                        sport_cats = ["strikeouts", "fantasy points", "walks allowed", "earned runs", "innings pitched"];
                    }
                    else{
                        sport_cats = ["rbis", "singles", "doubles", "triples", "homeruns", "walks", "runs", "fantasy points"];
                    }
                }
                else if (league == "nfl"){
                    if (player.positions.includes('QB')){
                        sport_cats = [ "pass yards", "pass tds", "rush yards", "rush tds", "fantasy points", "off interceptions", "attempts", "completions"];
                    }
                    else{
                        sport_cats = ["rush yards", "rush tds", "rec yards", "rec tds", "receptions", "fantasy points", "carries", "targets"];
                    }
                    
                }
                else if (league == "nhl"){
                    if (is_goalie){
                        sport_cats = ["saves", "shots against", "goals against", "fantasy points"];
                    }
                    else{
                        sport_cats = ["goals", "assists", "points", "shots", "blocked shots", "power play goals", "power play assists", "fantasy points"];
                    }
                }
                for (var cat in data[0].fields){
                    let cat_no_underscore = cat.replace(/_/g, " ");
                    if(cat == "fanduel_proj"){
                        cats["fantasy points"] = data[0].fields[cat];
                    }
                    else if (sport_cats.includes(cat_no_underscore)){
                        cats[cat_no_underscore] = data[0].fields[cat];
                    }
                }
                projections = cats;
                //add aggragate stats
                if (league == "nba"){
                    projections["steals+blocks"] = parseFloat(projections.steals) + parseFloat(projections.blocks);
                    projections["points+reb+ast"] = parseFloat(projections.points) + parseFloat(projections.rebounds) + parseFloat(projections.assists);
                }
                else if (league == "mlb"){
                    if(player.positions.includes('P')){
                        
                    }
                    else{
                        //hack total bases to give a better estimate for bets even though it's no longer accurate
                        projections["total bases"] = parseFloat(projections.singles) + parseFloat(projections.doubles) * 2 + parseFloat(projections.triples) * 2  + parseFloat(projections.homeruns) * 2;
                        projections["runs+rbis"] = parseFloat(projections.runs) + parseFloat(projections.rbis);
                        projections["hits+walks"] = parseFloat(projections.singles) + parseFloat(projections.doubles) + parseFloat(projections.triples) + parseFloat(projections.homeruns) + parseFloat(projections.walks);
                        projections["hits"] = parseFloat(projections.singles) + parseFloat(projections.doubles) + parseFloat(projections.triples) + parseFloat(projections.homeruns);
                    }
                }
                else if (league == "nhl" && !is_goalie){
                    projections["shots on goal"] = parseFloat(projections.shots);
                    projections["power play points"] = parseFloat(projections["power play goals"]) + parseFloat(projections["power play assists"]);
                    if (!parseFloat(projections.points)){
                        projections["points"] = parseFloat(projections.goals) + parseFloat(projections.assists);
                    }
                }
                else if (league == "nfl"){
                    if (player.positions.includes('QB')){
                        projections["total yards"] = parseFloat(projections["pass yards"]) + parseFloat(projections["rush yards"]);
                        projections["total tds"] = parseFloat(projections["pass tds"]) + parseFloat(projections["rush tds"]);
                        projections["interceptions"] = parseFloat(projections["off interceptions"]);
                    }
                    else{
                        projections["total yards"] = parseFloat(projections["rush yards"]) + parseFloat(projections["rec yards"]);
                        projections["total tds"] = parseFloat(projections["rush tds"]) + parseFloat(projections["rec tds"]);
                    }
                }
            }
        );
    }
    function buildPropCalcMetrics() {
        let totals = {};
        means = {};
        //check that player_stats has at least 5 entries
        if (player_stats.length < 5){
            //use the placeholder variances instead
            let vars = placeholder_variances;
            let formatted_vars = {};
            for (let stat in vars){
                if(player.positions.includes('P') && league == "mlb" && stat == "pitching_fantasy_points"){
                    formatted_vars["fantasy points"] = vars[stat];
                }
                else if(league == "nhl" && stat == "goalie_fantasy_points"){
                    if (is_goalie){
                        formatted_vars["fantasy points"] = vars[stat];
                    }
                }
                else if(league == "nhl" && is_goalie && stat == "fantasy_points"){
                    // the skater total; the goalie one is set above
                }
                else{
                    let formatted_stat_key = stat.replace(/_plus_/g, "+");
                    formatted_stat_key = formatted_stat_key.replace(/_/g, " ");
                    formatted_vars[formatted_stat_key] = vars[stat];
                }
            }
            variances = formatted_vars;
        }
        else{
            //calculate the variances for the stats
            for(var gamelog of player_stats){
                for(var stat in gamelog){
                    //if totals[stat] is undefined, set it to 0
                    if(totals[stat] === undefined){
                        totals[stat] = 0;
                    }
                    totals[stat] += parseFloat(gamelog[stat]);
                }
            }
            for(var stat in totals){
                means[stat] = totals[stat] / player_stats.length;
            }
            if (league == "nba"){
                means["steals+blocks"] = parseFloat(means.steals) + parseFloat(means.blocks);
                means["points+reb+ast"] = parseFloat(means.points) + parseFloat(means.rebounds) + parseFloat(means.assists);
            }
            else if (league == "mlb"){
                if(player.positions.includes('P')){
                    
                }
                else{
                    means["total bases"] = parseFloat(means.singles) + parseFloat(means.doubles) * 2 + parseFloat(means.triples) * 3  + parseFloat(means.homeruns) * 4;
                    means["runs+rbis"] = parseFloat(means.runs) + parseFloat(means.rbis);
                    means["hits"] = parseFloat(means.singles) + parseFloat(means.doubles) + parseFloat(means.triples) + parseFloat(means.homeruns);
                    means["hits+walks"] = parseFloat(means.singles) + parseFloat(means.doubles) + parseFloat(means.triples) + parseFloat(means.homeruns) + parseFloat(means.walks);

                }
            }
            else if (league == "nhl"){
                means["points"] = parseFloat(means.goals) + parseFloat(means.assists);
                means["shots on goal"] = parseFloat(means.shots);
                means["blocked shots"] = parseFloat(means.blocked_shots);
                means["power play points"] = parseFloat(means.power_play_goals) + parseFloat(means.power_play_assists);
                means["shots against"] = parseFloat(means.shots_against);
                means["goals against"] = parseFloat(means.goals_against);
            }
            else if (league == "nfl"){
                means["interceptions"] = parseFloat(means["off_interceptions"]);
                means["total_yards"] = parseFloat(means.pass_yards) + parseFloat(means.rush_yards) + parseFloat(means.rec_yards);
                means["total_tds"] = parseFloat(means.pass_tds) + parseFloat(means.rush_tds) + parseFloat(means.rec_tds);
            }
            //for each number: subtract the Mean and square the result then get the mean of this
            variances = {};
            if (league == "nba"){
                variances["points"] = 0;
                variances["rebounds"] = 0;
                variances["assists"] = 0;
                variances["steals"] = 0;
                variances["blocks"] = 0;
                variances["steals+blocks"] = 0;
                variances["points+reb+ast"] = 0;
                variances["fantasy points"] = 0;
            }
            else if (league == "mlb"){
                variances["fantasy points"] = 0;
                if(player.positions.includes('P')){
                    variances["strikeouts"] = 0;
                    variances["earned_runs"] = 0;
                    variances["innings_pitched"] = 0;
                    variances["walks_allowed"] = 0;
                }
                else{
                    variances["rbis"] = 0;
                    variances["runs"] = 0;
                    variances["runs+rbis"] = 0;
                    variances["total bases"] = 0;
                    variances["hits"] = 0;
                    variances["hits+walks"] = 0;
                }
            }
            else if (league == "nhl"){
                variances["fantasy points"] = 0;
                if (is_goalie){
                    variances["saves"] = 0;
                    variances["shots against"] = 0;
                    variances["goals against"] = 0;
                }
                else{
                    variances["goals"] = 0;
                    variances["assists"] = 0;
                    variances["points"] = 0;
                    variances["shots on goal"] = 0;
                    variances["blocked shots"] = 0;
                    variances["power play points"] = 0;
                }
            }
            else if (league == "nfl"){
                variances["fantasy points"] = 0;
                variances["pass_yards"] = 0;
                variances["rush_yards"] = 0;
                variances["rec_yards"] = 0;
                variances["total_yards"] = 0;
                variances["total_tds"] = 0;
                variances["receptions"] = 0;
                variances["pass_tds"] = 0;
                variances["rush_tds"] = 0;
                variances["rec_tds"] = 0;
                variances["interceptions"] = 0;
                variances["attempts"] = 0;
                variances["completions"] = 0;
                variances["carries"] = 0;
                variances["targets"] = 0;
            }
            for(var gamelog of player_stats){
                variances["fantasy points"] += Math.pow(parseFloat(gamelog["fantasy points"]) - means["fantasy points"], 2);
                if (league == "nba"){
                    variances["points"] += Math.pow(parseFloat(gamelog["points"]) - means["points"], 2);
                    variances["rebounds"] += Math.pow(parseFloat(gamelog["rebounds"]) - means["rebounds"], 2);
                    variances["assists"] += Math.pow(parseFloat(gamelog["assists"]) - means["assists"], 2);
                    variances["steals"] += Math.pow(parseFloat(gamelog["steals"]) - means["steals"], 2);
                    variances["blocks"] += Math.pow(parseFloat(gamelog["blocks"]) - means["blocks"], 2);
                    variances["points+reb+ast"] += Math.pow(parseFloat(gamelog["points"]) + parseFloat(gamelog["rebounds"]) + parseFloat(gamelog["assists"]) - parseFloat(means["points+reb+ast"]), 2);
                    variances["steals+blocks"] += Math.pow(parseFloat(gamelog["steals"]) + parseFloat(gamelog["blocks"]) - parseFloat(means["steals+blocks"]), 2);
                }
                else if (league == "mlb"){
                    if(player.positions.includes('P')){
                        variances["strikeouts"] += Math.pow(parseFloat(gamelog["strikeouts"]) - means["strikeouts"], 2);
                        variances["earned_runs"] += Math.pow(parseFloat(gamelog["earned_runs"]) - means["earned_runs"], 2);
                        variances["innings_pitched"] += Math.pow(parseFloat(gamelog["innings_pitched"]) - means["innings_pitched"], 2);
                        variances["walks_allowed"] += Math.pow(parseFloat(gamelog["walks_allowed"]) - means["walks_allowed"], 2);
                    }
                    else{
                        variances["rbis"] += Math.pow(parseFloat(gamelog["rbis"]) - means["rbis"], 2);
                        variances["runs"] += Math.pow(parseFloat(gamelog["runs"]) - means["runs"], 2);
                        variances["runs+rbis"] += Math.pow(parseFloat(gamelog["runs"]) + parseFloat(gamelog["rbis"]) - parseFloat(means["runs+rbis"]), 2);
                        variances["total bases"] += Math.pow(parseFloat(gamelog.singles) + parseFloat(gamelog.doubles) * 2 + parseFloat(gamelog.triples) * 3  + parseFloat(gamelog.homeruns) * 4 - parseFloat(means["total bases"]), 2);
                        variances["hits"] += Math.pow(parseFloat(gamelog.singles) + parseFloat(gamelog.doubles) + parseFloat(gamelog.triples) + parseFloat(gamelog.homeruns) - parseFloat(means["hits"]), 2);
                        variances["hits+walks"] += Math.pow(parseFloat(gamelog.singles) + parseFloat(gamelog.doubles) + parseFloat(gamelog.triples) + parseFloat(gamelog.homeruns) + parseFloat(gamelog.walks) - parseFloat(means["hits+walks"]), 2);
                    }
                }
                else if(league == "nhl"){
                    if (is_goalie){
                        variances["saves"] += Math.pow(parseFloat(gamelog["saves"]) - means["saves"], 2);
                        variances["shots against"] += Math.pow(parseFloat(gamelog["shots_against"]) - means["shots against"], 2);
                        variances["goals against"] += Math.pow(parseFloat(gamelog["goals_against"]) - means["goals against"], 2);
                    }
                    else{
                        variances["goals"] += Math.pow(parseFloat(gamelog["goals"]) - means["goals"], 2);
                        variances["assists"] += Math.pow(parseFloat(gamelog["assists"]) - means["assists"], 2);
                        variances["points"] += Math.pow(parseFloat(gamelog["goals"]) + parseFloat(gamelog["assists"]) - means["points"], 2);
                        variances["shots on goal"] += Math.pow(parseFloat(gamelog["shots"]) - means["shots on goal"], 2);
                        variances["blocked shots"] += Math.pow(parseFloat(gamelog["blocked_shots"]) - means["blocked shots"], 2);
                        variances["power play points"] += Math.pow(parseFloat(gamelog["power_play_goals"]) + parseFloat(gamelog["power_play_assists"]) - means["power play points"], 2);
                    }
                }
                else if(league == "nfl"){
                    variances["pass_yards"] += Math.pow(parseFloat(gamelog["pass_yards"]) - means["pass_yards"], 2);
                    variances["rush_yards"] += Math.pow(parseFloat(gamelog["rush_yards"]) - means["rush_yards"], 2);
                    variances["rec_yards"] += Math.pow(parseFloat(gamelog["rec_yards"]) - means["rec_yards"], 2);
                    variances["total_yards"] += Math.pow(parseFloat(gamelog["pass_yards"]) + parseFloat(gamelog["rush_yards"]) + parseFloat(gamelog["rec_yards"]) - parseFloat(means["total_yards"]), 2);
                    variances["total_tds"] += Math.pow(parseFloat(gamelog["pass_tds"]) + parseFloat(gamelog["rush_tds"]) + parseFloat(gamelog["rec_tds"]) - parseFloat(means["total_tds"]), 2);
                    variances["interceptions"] += Math.pow(parseFloat(gamelog["off_interceptions"]) - means["interceptions"], 2);
                    variances["receptions"] += Math.pow(parseFloat(gamelog["receptions"]) - means["receptions"], 2);
                    variances["pass_tds"] += Math.pow(parseFloat(gamelog["pass_tds"]) - means["pass_tds"], 2);
                    variances["rush_tds"] += Math.pow(parseFloat(gamelog["rush_tds"]) - means["rush_tds"], 2);
                    variances["rec_tds"] += Math.pow(parseFloat(gamelog["rec_tds"]) - means["rec_tds"], 2);
                    variances["attempts"] += Math.pow(parseFloat(gamelog["attempts"]) - parseFloat(means["attempts"]), 2);
                    variances["completions"] += Math.pow(parseFloat(gamelog["completions"]) - means["completions"], 2);
                    variances["carries"] += Math.pow(parseFloat(gamelog["carries"]) - means["carries"], 2);
                    variances["targets"] += Math.pow(parseFloat(gamelog["targets"]) - means["targets"], 2);
                }
            }
            for(var stat in variances){
                variances[stat] = variances[stat] / player_stats.length;
                //make sure the variance is not 0
                if(variances[stat] == 0){
                    variances[stat] = 0.25;
                }
            }
        }
    }
</script>
<style>
    .consistent-height .tab-content {
        display: flex;
    }

    .consistent-height .tab-content > .tab-pane {
        display: block; /* undo "display: none;" */
        visibility: hidden;
        margin-right: -100%;
        width: 100%;
    }

    .consistent-height .tab-content > .active {
        visibility: visible;
    }
</style>
<div>
    <div id="player-info-holder">
        <div class="d-flex px-2 pt-2 flex-md-row border-bottom">
            <div class="col-6 align-self-end">
                <div class="d-none d-lg-block" id="team-bg" style="height: 0px; position:relative; z-index:0; max-height:200px;">
                    {#if league == "mlb"}
                    <img style="position:absolute; aspect-ratio:1/1; opacity:.4; z-index:0; height:205px;" alt="{player.team.short_name}" id="team-photo" src="{player.team_img}" class="d-block mx-md-auto img-fluid" loading="lazy">
                    {:else}
                    <img style="position:absolute; top:-40px; opacity:.4; z-index:0; height:295px;" alt="{player.team.short_name}" id="team-photo" src="{player.team_img}" class="d-block mx-md-auto img-fluid" loading="lazy">
                    {/if}
                </div>
                <div id="player-photo-holder"  style="max-height: 200px; z-index:1; position:relative;">
                    <img style="max-height: 200px;" id="player-photo" onerror="this.src='https://dfstud.nyc3.digitaloceanspaces.com/dfstud/static/fantasy/img/brand/horse_default_profile2.png';" src="{player.img}" class="d-block mx-md-auto img-fluid" alt="{player.name}" loading="lazy">
                </div>
            </div>
            <div class="col-6 align-self-center">
                <h1 class="display-5 fw-bold lh-1 mb-1" style="overflow-x:clip;">{player.name}</h1>
                <SeasonAvgTable league={league} averages={means} is_goalie={is_goalie}/>
            </div>
        </div>
    </div>
    <div class="consistent-height bg-white" style="position: relative; z-index:1;">
        <ul class="nav nav-tabs" id="myTab" role="tablist">
            <li class="nav-item" role="presentation">
            <button class="nav-link active" id="prop-tab" data-bs-toggle="tab" data-bs-target="#prop" type="button" role="tab" aria-controls="home" aria-selected="true">Props &amp; Line Shop</button>
            </li>
            <li class="nav-item" role="presentation">
            <button class="nav-link" id="gamelog-tab" data-bs-toggle="tab" data-bs-target="#gamelog" type="button" role="tab" aria-controls="profile" aria-selected="false">Gamelog</button>
            </li>
        </ul>
        <div class="tab-content">
            <div class="tab-pane fade show active" id="prop" role="tabpanel" aria-labelledby="prop-tab">
                <div id="prop-calc-holder" class="bg-white" style="position: relative; z-index:1;">
                    {#if prop_metrics}
                        <PropCalculator premium_membership={premium_membership} league={league} stats={prop_metrics} props={props} fair_odds={fair_odds} market_lines={market_lines} gamelog={player_stats.slice(0, Math.min(player_stats.length, 5))}/>
                    {/if}
                </div>
            </div>
            <div class="tab-pane fade" id="gamelog" role="tabpanel" aria-labelledby="gamelog-tab">
                <div id="game-log-holder" class="bg-white" style="position: relative; z-index:1;">
                    {#if player_stats}
                        <Gamelog gamelog={player_stats} league={league} is_pitcher={player.positions.includes("P")} is_qb={player.positions.includes("QB")} is_goalie={is_goalie}/>
                    {/if}
                </div>
            </div>
        </div>
    </div>
</div>